import { useCallback, useEffect, useRef, useState } from "react";

export default function useSpeechDictation({
  language = "fr-FR",
  onTranscript,
} = {}) {
  const recognitionRef = useRef(null);
  const transcriptHandlerRef = useRef(onTranscript);
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    transcriptHandlerRef.current = onTranscript;
  }, [onTranscript]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const SpeechRecognition =
      window.SpeechRecognition ||
      window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setSupported(false);
      return undefined;
    }

    setSupported(true);

    const recognition =
      new SpeechRecognition();

    recognition.lang = language;
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onresult = (event) => {
      const transcript = Array.from(
        event.results || []
      )
        .map((result) =>
          result?.[0]?.transcript || ""
        )
        .join(" ")
        .trim();

      if (transcript) {
        transcriptHandlerRef.current?.(
          transcript
        );
      }
    };

    recognition.onend = () => {
      setListening(false);
    };

    recognition.onerror = () => {
      setListening(false);
    };

    recognitionRef.current =
      recognition;

    return () => {
      try {
        recognition.abort();
      } catch {
        // Le navigateur peut déjà avoir arrêté la reconnaissance.
      }

      recognitionRef.current = null;
    };
  }, [language]);

  const start = useCallback(() => {
    if (
      !supported ||
      !recognitionRef.current ||
      listening
    ) {
      return false;
    }

    try {
      recognitionRef.current.start();
      setListening(true);
      return true;
    } catch {
      return false;
    }
  }, [listening, supported]);

  const stop = useCallback(() => {
    if (!recognitionRef.current) {
      return;
    }

    try {
      recognitionRef.current.stop();
    } catch {
      // Aucun traitement supplémentaire nécessaire.
    }

    setListening(false);
  }, []);

  return {
    supported,
    listening,
    start,
    stop,
  };
}
